import { useMemo, useRef } from "react";
import { useAuth } from "@/core/auth/auth-provider";
import { createMemberInvoicesApi, type MemberInvoicesApi } from "./member-invoices";

/**
 * Member invoices API bound to the current standard-realm session. The bearer
 * is obtained lazily per request via `auth.getAccessToken()`, so the calling
 * surface must stay mounted while the provider is `refreshing`
 * (`ProtectedRoute keepMountedWhileRefreshing`, UI contract §0).
 *
 * The returned object is referentially stable for the component's lifetime:
 * the provider's `getAccessToken` changes identity on every session update,
 * and consumers key their load effects on the API object, so binding through
 * a ref prevents duplicate reads on bootstrap/refresh.
 */
export function useMemberInvoicesApi(): MemberInvoicesApi {
  const { getAccessToken } = useAuth();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  return useMemo(
    () =>
      createMemberInvoicesApi({
        getAccessToken: async () => {
          const token = await getAccessTokenRef.current();
          if (token === undefined) {
            // No bearer for this session: the API layer maps the rejection to
            // `token_unavailable`; the reason text is never surfaced.
            throw new Error("access token unavailable");
          }
          return token;
        },
      }),
    [],
  );
}
